import { Icon } from '@smartsash/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './CopyableId.module.css';

import { EMPTY_VALUE } from '@/utils/emptyValue';

/** How long the control shows the copied state before returning to rest. */
const COPIED_FEEDBACK_MS = 1500;

/**
 * A database id shown in full, next to a button that copies it (HS-1216).
 *
 * QA's use for the device id is operational, not decorative: an engineer reads it off the admin
 * panel and types it into `update-device-id.sh` over SSH to register a fume hood. That is why the
 * value wraps instead of being elided, and why the copy control exists at all — a 36-character
 * GUID retyped by hand is a transcription error waiting to happen.
 *
 * A separate icon button rather than making the whole cell a button: the id stays selectable text,
 * so a user can still highlight part of it, and the affordance to copy is visible rather than
 * discovered. The clipboard guards follow `packages/ui/src/components/VersionOverlay` — no
 * `navigator.clipboard` outside a secure context, and a rejected write must not claim success.
 */
export function CopyableId({ id, className }: { id: string; className?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  if (id === '') return <>{EMPTY_VALUE}</>;

  const classes = [styles.wrap, className].filter(Boolean).join(' ');

  // The DOM types declare `clipboard` as always present; it is absent outside a secure context.
  // Rather than render a control that silently does nothing, drop it — the id itself still has to
  // be readable there.
  const clipboard = navigator.clipboard as Clipboard | undefined;
  if (clipboard === undefined) {
    return (
      <span className={classes}>
        <span className={styles.value}>{id}</span>
      </span>
    );
  }

  const copy = () => {
    // Only confirm once the write actually resolved — a rejected permission must not report
    // success. A rejection leaves the control idle; the browser has already told the user why.
    void clipboard.writeText(id).then(
      () => {
        setCopied(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          setCopied(false);
        }, COPIED_FEEDBACK_MS);
      },
      () => {
        /* clipboard unavailable or denied — stay idle */
      },
    );
  };

  return (
    <span className={classes}>
      <span className={styles.value}>{id}</span>
      <button
        type="button"
        className={[styles.copy, copied ? styles.copied : ''].filter(Boolean).join(' ')}
        aria-label={copied ? t('common.copyId.copied') : t('common.copyId.aria', { id })}
        onClick={copy}
      >
        <Icon name={copied ? 'check' : 'copy-01'} size={14} />
      </button>
    </span>
  );
}

export default CopyableId;
